import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Image,
  Divider,
  HStack,
  Link as ChakraLink,
  useColorModeValue,
  IconButton,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FaLinkedin, FaXTwitter, FaYoutube } from "react-icons/fa6";

export default function Footer() {
  const location = useLocation();

  // EN/FR prefix support (keeps your visible link labels/content unchanged)
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";
  const withBase = (path) => {
    if (!path || path === "/") return `${base}/`;
    return `${path.startsWith("/") ? path : `/${path}`}`;
  };

  // Professional research-portal styling (less bright, cleaner hierarchy)
  const bg = useColorModeValue("#3b4b70", "#0B1220");
  const border = useColorModeValue("rgba(255,255,255,0.10)", "rgba(255,255,255,0.10)");
  const text = useColorModeValue("#E5E7EB", "#E5E7EB");
  const sub = useColorModeValue("rgba(229,231,235,0.78)", "rgba(229,231,235,0.78)");
  const accent = "#06B6D4";

const panelBg = useColorModeValue(
  "rgba(62, 103, 66, 0.18)",   // much brighter
  "rgba(255,255,255,0.12)"
);

const panelBorder = useColorModeValue(
  "rgba(255,255,255,0.35)",
  "rgba(255,255,255,0.25)"
);

  const linkProps = {
    as: RouterLink,
    fontWeight: "700",
    fontSize: "sm",
    color: sub,
    _hover: { color: accent, textDecoration: "none" },
  };

  return (
    <Box bg={bg} borderTop="1px solid" borderColor={border}>
      {/* subtle accent line */}
      <Box
        h="3px"
        bg={`linear-gradient(90deg, ${accent} 0%, rgba(34,211,238,0.35) 45%, rgba(34,211,238,0) 100%)`}
      />

      <Container maxW="1200px" py={{ base: 6, md: 8 }}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, md: 6 }}>
          {/* Brand / Acknowledgement (same content, upgraded layout) */}
          <Stack spacing={4}>
            <HStack spacing={3} align="center">
              <Box
                bg={panelBg}
                border="1px solid"
                borderColor={panelBorder}
                borderRadius="16px"
                px={3}
                py={2}
              >
                <Image
                  src="traversal_logo.png"
                  alt="TRAVERSAL"
                  h="40px"
                  w="auto"
                  objectFit="contain"
                  draggable={false}
                />
              </Box>

              <Box lineHeight={1.05}>
                <Text fontWeight="900" fontSize="lg" color={text}>
                  TRAVERSAL
                </Text>
                <Text fontSize="sm" color={sub}>
                  Training and Research in Autonomous Vehicles
                </Text>
              </Box>
            </HStack>

            <Box
              bg={panelBg}
              border="1px solid"
              borderColor={panelBorder}
              borderRadius="16px"
              p={4}
            >
              <HStack spacing={3} align="flex-start">
                <Image
                  src="NSERC_RGB.png"
                  alt="Ontario Research Fund"
                  h="64px"
                  w="auto"
                  objectFit="contain"
                  flexShrink={0}
                  draggable={false}
                />
                <Text fontSize="sm" color={sub} textAlign="left" lineHeight={1.75}>
                  We acknowledge the support of the Natural Sciences and Engineering Research Council of Canada (NSERC).
                </Text>
              </HStack>
            </Box>
          </Stack>

          {/* Quick Links (same link labels/content) */}
          <Stack spacing={4}>
            <Text
              fontWeight="900"
              color={text}
              letterSpacing="0.10em"
              textTransform="uppercase"
              fontSize="sm"
            >
              Quick Links
            </Text>

            <SimpleGrid columns={2} spacing={3}>
              <ChakraLink {...linkProps} to={("/")}>
                Home
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/about-us")}>
                Team
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/program")}>
                Program
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/seminars")}>
                Seminars
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/publications")}>
                Publications
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/news")}>
                News
              </ChakraLink>
              <ChakraLink {...linkProps} to={withBase("/contact-us")}>
                Contact
              </ChakraLink>
            </SimpleGrid>
          </Stack>

          {/* Connect / Social (same text content; social icons added) */}
          <Stack spacing={4}>
            <Text
              fontWeight="900"
              color={text}
              letterSpacing="0.10em"
              textTransform="uppercase"
              fontSize="sm"
            >
              Connect
            </Text>

            <Box
              bg={panelBg}
              border="1px solid"
              borderColor={panelBorder}
              borderRadius="16px"
              p={4}
            >
              <Stack spacing={2}>
                <Text fontSize="sm" color={sub} lineHeight={1.7}>
                  University of Ottawa • TRAVERSAL
                </Text>
                {/* If you have an email, add it here */}
                {/* <Text fontSize="sm" color={sub}>Email: sitecav@uottawa.ca</Text> */}
                <Text fontSize="sm" color={sub} lineHeight={1.7}>
                  Collaborations: academia • industry • public sector
                </Text>

                
              </Stack>
            </Box>
          </Stack>
        </SimpleGrid>

        <Divider my={{ base: 5, md: 6 }} borderColor={border} />

        {/* Bottom strip (same content) */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} alignItems="center">
          <Text fontSize="sm" color={sub}>
            © {new Date().getFullYear()} TRAVERSAL. All rights reserved.
          </Text>

          <Text
            fontSize="sm"
            color={sub}
            textAlign={{ base: "left", md: "right" }}
            fontWeight="700"
            letterSpacing="0.02em"
          >
            Secure, Intelligent & Trustworthy Ecosystems for <br /> Connected and Autonomous Vehicles
          </Text>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
